import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { enforceHttpsRedirect } from './utils/https';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SetupGuard } from './components/auth/SetupGuard';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { SuperUserSetupPage } from './pages/SuperUserSetupPage';
import OnboardingPage from './pages/OnboardingPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { SearchPage } from './pages/SearchPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { OrganizationCreatePage } from './pages/organizations/OrganizationCreatePage';
import { OrganizationEditPage } from './pages/organizations/OrganizationEditPage';
import { OrganizationDetailPage } from './pages/organizations/OrganizationDetailPage';
import { StakeholderListPage } from './pages/stakeholders/StakeholderListPage';
import { StakeholderDetailPage } from './pages/stakeholders/StakeholderDetailPage';
import { StakeholderCreatePage } from './pages/stakeholders/StakeholderCreatePage';
import { StakeholderEditPage } from './pages/stakeholders/StakeholderEditPage';
import { WikiHomePage } from './pages/wiki/WikiHomePage';
import { WikiViewPage } from './pages/wiki/WikiViewPage';
import { WikiCreatePage } from './pages/wiki/WikiCreatePage';
import { WikiEditPage } from './pages/wiki/WikiEditPage';
import { MediaPage } from './pages/MediaPage';
import { AuditPage } from './pages/AuditPage';
import { AccessControlPage } from './pages/AccessControlPage';
import { PendingEntriesPage } from './pages/PendingEntriesPage';
import { RelationshipsPage } from './pages/RelationshipsPage';
import { TaxonomiesPage } from './pages/TaxonomiesPage';
import { InfluenceGraphPage } from './pages/InfluenceGraphPage';
import { MonitoringPage } from './pages/MonitoringPage';
import { GovernmentPage } from './pages/GovernmentPage';
import { DuplicatesPage } from './pages/DuplicatesPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { IssuesListPage } from './pages/issues/IssuesListPage';
import { IssueWorkspacePage } from './pages/issues/IssueWorkspacePage';
import { AdminPage } from './pages/AdminPage';
import { AISettingsPage } from './pages/admin/AISettingsPage';
import { ErrorBoundary, ToastProvider } from './components/common';
import { ScrollToTop } from './components/common/ScrollToTop';
import { errorReportingService } from './services/errorReportingService';

function App() {
  // Enforce HTTPS redirect on app load
  useEffect(() => {
    enforceHttpsRedirect();
  }, []);

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        errorReportingService.reportComponentError(error, errorInfo, 'critical');
      }}
    >
      <ToastProvider>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <SetupGuard>
              <div className="App">
                <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/setup" element={<SuperUserSetupPage />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users" 
                  element={
                    <ProtectedRoute>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search"
                  element={
                    <ProtectedRoute>
                      <SearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations"
                  element={
                    <ProtectedRoute>
                      <OrganizationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations/new"
                  element={
                    <ProtectedRoute>
                      <OrganizationCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations/:id"
                  element={
                    <ProtectedRoute>
                      <OrganizationDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations/:id/edit"
                  element={
                    <ProtectedRoute>
                      <OrganizationEditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stakeholders"
                  element={
                    <ProtectedRoute>
                      <StakeholderListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stakeholders/new"
                  element={
                    <ProtectedRoute>
                      <StakeholderCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stakeholders/:id"
                  element={
                    <ProtectedRoute>
                      <StakeholderDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stakeholders/:id/edit"
                  element={
                    <ProtectedRoute>
                      <StakeholderEditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wiki"
                  element={
                    <ProtectedRoute>
                      <WikiHomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wiki/new"
                  element={
                    <ProtectedRoute>
                      <WikiCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wiki/:id"
                  element={
                    <ProtectedRoute>
                      <WikiViewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wiki/:id/edit"
                  element={
                    <ProtectedRoute>
                      <WikiEditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/media"
                  element={
                    <ProtectedRoute>
                      <MediaPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/relationships"
                  element={
                    <ProtectedRoute>
                      <RelationshipsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/taxonomies"
                  element={
                    <ProtectedRoute>
                      <TaxonomiesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <ProtectedRoute>
                      <AuditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/access-control"
                  element={
                    <ProtectedRoute>
                      <AccessControlPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pending"
                  element={
                    <ProtectedRoute>
                      <PendingEntriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/duplicates"
                  element={
                    <ProtectedRoute>
                      <DuplicatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/issues"
                  element={
                    <ProtectedRoute>
                      <IssuesListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/issues/:slug"
                  element={
                    <ProtectedRoute>
                      <IssueWorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/monitoring"
                  element={
                    <ProtectedRoute>
                      <MonitoringPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/government"
                  element={
                    <ProtectedRoute>
                      <GovernmentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/influence-graph"
                  element={
                    <ProtectedRoute>
                      <InfluenceGraphPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
        <Route
          path="/ai-settings"
          element={
            <ProtectedRoute>
              <AISettingsPage />
            </ProtectedRoute>
          }
        />
                
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </SetupGuard>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
